import { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";

// Staff Pages
import StaffLogin from "./pages/staff/StaffLogin";
import StaffSignup from "./pages/staff/StaffSignup";
import PendingApproval from "./pages/staff/PendingApproval";
import ForgotPassword from "./pages/staff/ForgotPassword";
import ForgotUsername from "./pages/staff/ForgotUsername";
import Profile from "./pages/staff/Profile";
import PromoBoostPortal from "./pages/PromoBoostPortal";
import PromoPaymentSuccess from "./pages/promo/PromoPaymentSuccess";
import PromoCampaignDashboard from "./pages/promo/PromoCampaignDashboard";
import Dashboard from "./pages/Dashboard";
import Money from "./pages/Money";
import Clients from "./pages/Clients";
import Projects from "./pages/Projects";
import Content from "./pages/Content";
import Team from "./pages/Team";
import Documents from "./pages/Documents";
import Settings from "./pages/Settings";
import UserManagement from "./pages/staff/UserManagement";
import Tasks from "./pages/Tasks";
import AdminSubmissions from "./pages/staff/AdminSubmissions";
import AdminQRCodes from "./pages/staff/AdminQRCodes";
import StaffApprovals from "./pages/staff/StaffApprovals";
import Messages from "./pages/staff/Messages";

// Customer Pages
import CustomerLogin from "./pages/customer/CustomerLogin";
import ClientSignup from "./pages/customer/ClientSignup";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerInvoices from "./pages/customer/CustomerInvoices";
import CustomerProjects from "./pages/customer/CustomerProjects";
import CustomerProjectDocuments from "./pages/customer/CustomerProjectDocuments";
import CustomerMessages from "./pages/customer/CustomerMessages";
import PromoCampaignQueue from "./pages/staff/PromoCampaignQueue";

// Shared
import PaymentSuccess from "./pages/PaymentSuccess";
import PortalSelector from "./pages/PortalSelector";

// Public Pages
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Booking from "./pages/Booking";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import PasswordReset from "./pages/PasswordReset";
import Membership from "./pages/Membership";
import MembershipSuccess from "./pages/MembershipSuccess";
import MembershipCancel from "./pages/MembershipCancel";

// Hooks
import useSessionTimeout from "./hooks/useSessionTimeout";

// Components
import Sidebar from "./components/Sidebar";
import CustomerSidebar from "./components/CustomerSidebar";
import CinematicPortalLayout from "./components/CinematicPortalLayout";
import { Toaster } from "./components/ui/sonner";

// Import cinematic design system
import "./styles/cinematic-design-system.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Staff Auth Callback
const StaffAuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API}/auth/staff/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const user = await response.json();
            if (user.status === "pending") {
              navigate("/staff/pending", { state: { user }, replace: true });
            } else if (user.status === "active") {
              navigate("/dashboard", { state: { user }, replace: true });
            } else {
              navigate("/staff/login", { state: { error: `Account ${user.status}` }, replace: true });
            }
          } else {
            navigate("/staff/login", { replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/staff/login", { replace: true });
        }
      } else {
        navigate("/staff/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#A1A1AA]">Authenticating...</p>
      </div>
    </div>
  );
};

// Customer Auth Callback
const CustomerAuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API}/auth/customer/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const customer = await response.json();
            navigate("/client/dashboard", { state: { customer }, replace: true });
          } else {
            navigate("/client/login", { replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/client/login", { replace: true });
        }
      } else {
        navigate("/client/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#A1A1AA]">Authenticating...</p>
      </div>
    </div>
  );
};

// Staff Protected Route
const StaffProtectedRoute = ({ children, requireAdmin = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Admin roles that can access /admin/* routes
  const ADMIN_ROLES = ["owner", "co_ceo", "super_admin", "staff_admin", "treasurer", "director"];

  // Session timeout handler
  const handleSessionTimeout = useCallback(() => {
    // Clear auth state and redirect to login
    setIsAuthenticated(false);
    setUser(null);
    // Clear cookie by calling logout
    fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    navigate("/staff/login", { state: { error: "Session expired due to inactivity" }, replace: true });
  }, [navigate]);

  // Use session timeout hook
  useSessionTimeout(handleSessionTimeout);

  // Handle navigation state synchronously (avoid setState inside an effect)
  if (location.state?.user && !user) {
    setUser(location.state.user);
    if (location.state.user.status === "active") {
      setIsAuthenticated(true);
    } else if (location.state.user.status === "pending") {
      navigate("/staff/pending", { state: { user: location.state.user }, replace: true });
    } else if (location.state.user.status === "denied") {
      navigate("/staff/login", { state: { error: "Your account has been denied access" }, replace: true });
    } else {
      navigate("/staff/login", { state: { error: `Account status: ${location.state.user.status}` }, replace: true });
    }
  }

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/staff/me`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          
          // Status-based gating
          if (userData.status === "pending") {
            navigate("/staff/pending", { state: { user: userData }, replace: true });
          } else if (userData.status === "denied") {
            navigate("/staff/login", { state: { error: "Your account has been denied access" }, replace: true });
          } else if (userData.status === "disabled") {
            navigate("/staff/login", { state: { error: "Your account has been disabled" }, replace: true });
          } else if (userData.status === "active") {
            // Role-based gating for admin routes
            if (requireAdmin && !ADMIN_ROLES.includes(userData.role)) {
              navigate("/dashboard", { replace: true });
            } else {
              setIsAuthenticated(true);
            }
          } else {
            navigate("/staff/login", { state: { error: `Account status: ${userData.status}` }, replace: true });
          }
        } else {
          setIsAuthenticated(false);
          navigate("/staff/login", { replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/staff/login", { replace: true });
      }
    };

    // If navigation state already authenticated a user, skip fetch
    if (location.state?.user) return;
    checkAuth();
  }, [navigate, location.state, requireAdmin, ADMIN_ROLES]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

// Customer Protected Route
const CustomerProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle navigation state synchronously (avoid setState inside an effect)
  if (location.state?.customer && !customer) {
    setCustomer(location.state.customer);
    setIsAuthenticated(true);
  }

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/customer/me`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const customerData = await response.json();
          setCustomer(customerData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate("/client/login", { replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/client/login", { replace: true });
      }
    };

    // If navigation state already authenticated a customer, skip fetch
    if (location.state?.customer) return;
    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ customer, setCustomer });
};

// Staff Layout - Cinematic Portal Theme
const StaffLayout = ({ children, user }) => {
  return (
    <CinematicPortalLayout>
      <div className="flex min-h-screen">
        <Sidebar user={user} />
        <main className="flex-1 md:ml-64 p-6 overflow-auto pt-16 md:pt-6">
          {children}
        </main>
      </div>
    </CinematicPortalLayout>
  );
};

// Customer Layout - Cinematic Portal Theme
const CustomerLayout = ({ children, customer }) => {
  return (
    <CinematicPortalLayout>
      <div className="flex min-h-screen">
        <CustomerSidebar customer={customer} />
        <main className="flex-1 md:ml-64 p-6 overflow-auto pt-16 md:pt-6">
          {children}
        </main>
      </div>
    </CinematicPortalLayout>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment for staff auth
  if (location.pathname === "/staff/callback" || (location.pathname === "/dashboard" && location.hash?.includes("session_id="))) {
    return <StaffAuthCallback />;
  }
  
  // Check for session_id in URL fragment for customer auth
  if (location.pathname === "/client/callback" || (location.pathname === "/client/dashboard" && location.hash?.includes("session_id="))) {
    return <CustomerAuthCallback />;
  }

  return (
    <Routes>
      {/* Portal Selection / Landing Page */}
      <Route path="/" element={<PortalSelector />} />

      {/* Public Pages */}
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/auth/reset-password" element={<PasswordReset />} />

      {/* Membership Routes (public) */}
      <Route path="/membership" element={<Membership />} />
      <Route path="/membership/success" element={<MembershipSuccess />} />
      <Route path="/membership/cancel" element={<MembershipCancel />} />

      {/* Promo Boost Portal (public) */}
      <Route path="/promo" element={<PromoBoostPortal />} />
      <Route path="/promo/payment-success" element={<PromoPaymentSuccess />} />
      <Route path="/promo/campaign/:campaignId" element={<PromoCampaignDashboard />} />
      
      {/* Staff Routes */}
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff/signup" element={<StaffSignup />} />
      <Route path="/staff/pending" element={<PendingApproval />} />
      <Route path="/staff/forgot-password" element={<ForgotPassword />} />
      <Route path="/staff/forgot-username" element={<ForgotUsername />} />
      <Route path="/staff/callback" element={<StaffAuthCallback />} />
      
      <Route
        path="/profile"
        element={
          <StaffProtectedRoute>
            {({ user, setUser }) => (
              <StaffLayout user={user}>
                <Profile user={user} setUser={setUser} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Dashboard user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Messages user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/money"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Money user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Clients user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Projects user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/content"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Content user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Team user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Documents user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Settings user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <StaffProtectedRoute requireAdmin={true}>
            {({ user }) => (
              <StaffLayout user={user}>
                <UserManagement user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/admin/promo/campaigns"
        element={
          <StaffProtectedRoute requireAdmin={true}>
            {({ user }) => (
              <StaffLayout user={user}>
                <PromoCampaignQueue user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/admin/submissions"
        element={
          <StaffProtectedRoute requireAdmin={true}>
            {({ user }) => (
              <StaffLayout user={user}>
                <AdminSubmissions user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/admin/qr-codes"
        element={
          <StaffProtectedRoute requireAdmin={true}>
            {({ user }) => (
              <StaffLayout user={user}>
                <AdminQRCodes user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/admin/staff-approvals"
        element={
          <StaffProtectedRoute requireAdmin={true}>
            {({ user }) => (
              <StaffLayout user={user}>
                <StaffApprovals user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/tasks"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Tasks user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      <Route
        path="/tasks/:taskId"
        element={
          <StaffProtectedRoute>
            {({ user }) => (
              <StaffLayout user={user}>
                <Tasks user={user} />
              </StaffLayout>
            )}
          </StaffProtectedRoute>
        }
      />

      {/* Customer Routes */}
      <Route path="/client/login" element={<CustomerLogin />} />
      <Route path="/client/signup" element={<ClientSignup />} />
      <Route path="/client/callback" element={<CustomerAuthCallback />} />
      
      <Route
        path="/client/dashboard"
        element={
          <CustomerProtectedRoute>
            {({ customer }) => (
              <CustomerLayout customer={customer}>
                <CustomerDashboard customer={customer} />
              </CustomerLayout>
            )}
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/client/invoices"
        element={
          <CustomerProtectedRoute>
            {({ customer }) => (
              <CustomerLayout customer={customer}>
                <CustomerInvoices />
              </CustomerLayout>
            )}
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/client/projects"
        element={
          <CustomerProtectedRoute>
            {({ customer }) => (
              <CustomerLayout customer={customer}>
                <CustomerProjects />
              </CustomerLayout>
            )}
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/client/documents"
        element={
          <CustomerProtectedRoute>
            {({ customer }) => (
              <CustomerLayout customer={customer}>
                <CustomerProjectDocuments />
              </CustomerLayout>
            )}
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/client/messages"
        element={
          <CustomerProtectedRoute>
            {({ customer }) => (
              <CustomerLayout customer={customer}>
                <CustomerMessages customer={customer} />
              </CustomerLayout>
            )}
          </CustomerProtectedRoute>
        }
      />

      {/* Shared */}
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/client/payment-success" element={<PaymentSuccess />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
